import type { DragEventHandler, KeyboardEventHandler, MouseEventHandler, ReactNode } from 'react';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';

interface UploadDropzoneCardProps {
  title: string;
  subtitle: string;
  selectFileButton: ReactNode;
  isDragging?: boolean;
  ariaLabel: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDragOver?: DragEventHandler<HTMLDivElement>;
  onDragLeave?: DragEventHandler<HTMLDivElement>;
  onDrop?: DragEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

export function UploadDropzoneCard({
  title,
  subtitle,
  selectFileButton,
  isDragging = false,
  ariaLabel,
  onClick,
  onDragOver,
  onDragLeave,
  onDrop,
  onKeyDown,
}: UploadDropzoneCardProps) {
  return (
    <div
      className={`border-2 border-dashed rounded-xl p-12 flex flex-col items-center gap-4 bg-muted/20 hover:bg-muted/30 transition-colors cursor-pointer ${isDragging ? 'border-primary bg-primary/5' : 'border-border'}`}
      data-testid="drop-zone"
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      role="button"
      aria-label={ariaLabel}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
        <Upload className="h-7 w-7 text-primary" />
      </div>
      <div className="text-center">
        <p className="text-[15px] font-semibold text-foreground">{title}</p>
        <p className="text-[13px] text-muted-foreground mt-1">{subtitle}</p>
      </div>
      {selectFileButton}
    </div>
  );
}

interface SelectedImportFileCardProps {
  fileName: string;
  metaText: string;
  statusText?: string;
  removeButton?: ReactNode;
}

export function SelectedImportFileCard({
  fileName,
  metaText,
  statusText,
  removeButton,
}: SelectedImportFileCardProps) {
  return (
    <div
      className="rounded-lg border border-border bg-card p-4 flex items-center gap-4"
      data-testid="file-preview"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 shrink-0">
        <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-[13px] font-semibold truncate" data-testid="file-name">
          {fileName}
        </p>
        <p className="text-xs text-muted-foreground">{metaText}</p>
        {statusText !== undefined ? (
          <p className="text-xs font-medium text-emerald-700" data-testid="template-status">
            {statusText}
          </p>
        ) : null}
      </div>
      {removeButton}
    </div>
  );
}

interface TemplateDownloadHintProps {
  prefixText: string;
  downloadLink: ReactNode;
  hintText?: string;
}

export function TemplateDownloadHint({
  prefixText,
  downloadLink,
  hintText,
}: TemplateDownloadHintProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Download className="h-4 w-4 shrink-0" />
        <span>{prefixText}</span>
        {downloadLink}
      </div>
      {hintText !== undefined ? (
        <p className="text-xs text-muted-foreground/70 pl-6">{hintText}</p>
      ) : null}
    </div>
  );
}
